import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { FaHome, FaClipboardList, FaPlusCircle, FaUsers, FaCogs, FaTint, FaSignOutAlt, FaTruck, FaBell, FaBars, FaTimes, FaUserShield } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { NumberBadge } from './ui/Badge';
import { MARCA } from '../config/marca';

export default function Navbar() {
  const user = useAuthStore(state => state.user);
  const puede = useAuthStore(state => state.puede);
  const logout = useAuthStore(state => state.logout);
  const location = useLocation();
  const [notificationCount, setNotificationCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!user) return;

    const cargarNotificaciones = async () => {
      if (puede('aprobar_registros')) {
        // Contar registros pendientes para admin
        const { count } = await supabase
          .from('registros_pendientes')
          .select('*', { count: 'exact', head: true });
        setNotificationCount(count || 0);
      }

      if (user.role === 'secretaria') {
        // Contar registros aprobados sin guía para secretaria
        const { count } = await supabase
          .from('registros')
          .select('*', { count: 'exact', head: true })
          .is('guia', null);
        setNotificationCount(count || 0);
      }
    };

    // Carga inicial
    cargarNotificaciones();

    // Actualizar cada 2 minutos, pero SOLO si la pestaña está visible.
    // Si el usuario deja la app abierta en segundo plano, no se hacen
    // consultas: así no se gasta la cuota del plan gratuito de Supabase.
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        cargarNotificaciones();
      }
    }, 120000);

    // Al volver a la pestaña, refrescar de inmediato para ver datos al día
    const alVolver = () => {
      if (document.visibilityState === 'visible') {
        cargarNotificaciones();
      }
    };
    document.addEventListener('visibilitychange', alVolver);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [user]);

  // Cerrar navbar en móvil al cambiar de ruta
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const iconColors = {
    '/dashboard': 'text-sky-600',
    '/registros': 'text-blue-600',
    '/registro-toner': 'text-green-600',
    '/envio-cliente': 'text-cyan-600',
    '/clientes': 'text-amber-600',
    '/maquinas': 'text-blue-600',
    '/toners': 'text-pink-600',
    '/historial-envios': 'text-indigo-600',
    '/usuarios': 'text-purple-600',
  };

  const links = [
    { to: '/dashboard', label: 'Inicio', icon: <FaHome className="text-xl" /> },
    { to: '/registros', label: 'Registros', icon: <FaClipboardList className="text-xl" /> },
    { to: '/registro-toner', label: 'Nuevo Registro', icon: <FaPlusCircle className="text-xl" /> },
    { to: '/envio-cliente', label: 'Envío Cliente', icon: <FaTruck className="text-xl" />},
    { to: '/clientes', label: 'Clientes', icon: <FaUsers className="text-xl" /> },
  ];

  // Cada sección se muestra según el permiso correspondiente.
  // Los administradores tienen todos los permisos automáticamente
  // (lo resuelve la función `puede` del authStore).
  if (puede('gestionar_maquinas')) {
    links.push({ to: '/maquinas', label: 'Máquinas', icon: <FaCogs className="text-xl" /> });
  }
  if (puede('gestionar_toners')) {
    links.push({ to: '/toners', label: 'Toners', icon: <FaTint className="text-xl" /> });
  }
  if (puede('ver_historial')) {
    links.push({ to: '/historial-envios', label: 'Historial Envios', icon: <FaTruck className="text-xl" /> });
  }
  // La gestión de usuarios es exclusiva de administradores
  if (user?.role === 'admin') {
    links.push({ to: '/usuarios', label: 'Usuarios', icon: <FaUserShield className="text-xl" /> });
  }

  return (
    <>
      {/* Botón hamburguesa para móvil */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
        className="lg:hidden fixed top-3 left-3 z-50 p-2.5 rounded-xl bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:scale-105"
      >
        {isOpen ? (
          <FaTimes className="text-lg text-gray-700" />
        ) : (
          <FaBars className="text-lg text-gray-700" />
        )}
      </button>

      {/* Overlay para móvil */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 animate-fadeIn"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Navbar */}
      <nav
        className={`
          fixed top-0 left-0 h-full w-64 lg:w-72
          bg-gradient-to-b from-white to-gray-50
          shadow-2xl
          flex flex-col
          z-40
          border-r border-gray-200
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Contenido desplazable: si la pantalla es baja, se puede hacer scroll
            en lugar de que el menú quede cortado (antes había que reducir el zoom). */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-4 lg:p-6">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <img
              src={MARCA.rutaLogo}
              alt={MARCA.textoAltLogo}
              className="max-h-14 lg:max-h-20 w-auto object-contain"
            />
            {notificationCount > 0 && (
              <div className="relative">
                <div className="p-2 lg:p-3 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl">
                  <FaBell className="text-amber-600 text-xl lg:text-2xl animate-pulse" />
                </div>
                <NumberBadge count={notificationCount} max={99} />
              </div>
            )}
          </div>

          {/* Información del usuario */}
          <div className="mb-4 lg:mb-5 p-3 rounded-xl bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-100">
            <p className="text-xs font-medium text-sky-600 mb-0.5 uppercase tracking-wide">
              {user?.role === 'admin' ? 'Administrador' : 'Secretaria'}
            </p>
            <p className="text-sm font-semibold text-gray-800 truncate">{user?.email}</p>
          </div>

          {/* Links de navegación */}
          <nav className="space-y-1">
            {links.map(({ to, label, icon }) => {
              const isActive = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  className={`
                    group relative flex items-center gap-3 px-3 py-2.5 rounded-xl
                    transition-all duration-300
                    ${
                      isActive
                        ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-200'
                        : 'text-gray-700 hover:bg-white hover:shadow-md'
                    }
                  `}
                >
                  {/* Indicador de página activa */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-7 bg-white rounded-r-full" />
                  )}

                  {/* Icono con color */}
                  <div
                    className={`
                      flex-shrink-0 transition-transform duration-300
                      ${isActive ? 'text-white scale-110' : iconColors[to] || 'text-gray-500'}
                      ${!isActive && 'group-hover:scale-110'}
                    `}
                  >
                    {icon}
                  </div>

                  {/* Label */}
                  <span
                    className={`
                      font-medium text-sm lg:text-base truncate
                      ${isActive ? 'text-white' : 'text-gray-700'}
                    `}
                  >
                    {label}
                  </span>

                  {/* Efecto de flecha en hover para items no activos */}
                  {!isActive && (
                    <svg
                      className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transform translate-x-0 group-hover:translate-x-1 transition-all duration-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Footer con botón de logout (siempre visible, no se desplaza) */}
        <div className="flex-shrink-0 p-4 lg:p-5 border-t border-gray-200 bg-gray-50">
          <button
            onClick={logout}
            className="
              w-full flex items-center justify-center gap-3
              px-4 py-2.5 rounded-xl
              bg-gradient-to-r from-red-500 to-orange-500
              text-white font-medium text-sm lg:text-base
              shadow-md hover:shadow-xl
              transition-all duration-300
              transform hover:scale-105 active:scale-95
            "
          >
            <FaSignOutAlt className="text-lg" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </nav>
    </>
  );
}
